"use client";
import BusinessType from "@/app/models/BusinessType";
import { debounce } from 'lodash';
import { AuthUser } from "@/app/models/Profile"
import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useRef, useState } from "react";
import PlanDescription from "./PlanDescription";
import FormHelper from "@/app/lib/ClientFormHelper.client";
import { logDebug, logError } from '@/app/lib/logger';
import { PlanName } from "@/app/pricing/planTypes";
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe, StripeError } from '@stripe/stripe-js';
import { useElements, useStripe, PaymentElement, AddressElement } from "@stripe/react-stripe-js";
import { z } from "zod";
import ajaxAnimatedBar from "@/app/images/ajax_animated_bar.gif";
import clsx from "clsx";
import { avenirNextBold, avenirNextMedium, avenirNextRegular } from "@/styles/fonts";
import { AdbeatButton } from "@/app/components/adbeatButton";
import glyphiconFont from "@/styles/glyphicon-font";
import TurnstileWidget, { type TurnstileWidgetHandle } from "@/app/components/TurnstileWidget.client";
import {
    shouldDisableSignupEmailField,
    shouldDisableSignupPasswordFields,
} from "./signupAccess";

function formatMoney(cents: number) {
    return `$${(cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
}

const headingClassName = clsx(
    avenirNextMedium.className,
    "text-[22px]",
    "text-[#354052]",
)

const formGroupClassName = clsx(
    "flex",
    "flex-col",
    "mb-[10px]",
)

const labelClassName = clsx(
    "uppercase",
    "text-[#A3B3BF]",
    "text-[11px]",
    "md:text-[12px]",
    "pb-[3px]",
    "font-bold",
    avenirNextRegular.className,
)

const inputClassName = clsx(
    "border",
    "border-[#ebf1f5]",
    "p-[10px]",
    "text-[14px]",
    "text-[#999999]",
    "rounded",
    "outline-none",
)

export default function SignupFormWrapper(props: {
    user: AuthUser,
    stripePubKey: string,
    product: any,
    planName: PlanName,
    isCaptchaEnabled: boolean,
    isDevMode: boolean,
    productPriceInCents: number,
    statesAndCountries: { [key: string]: { name: string, states: { [key: string]: string }, phonecode: string } },
    showCouponByDefault?: boolean,
    promotionalMessage?: string,
    defaultCouponCode?: string,
    discountedPriceCents?: number,
}) {
    const planMatch = props.planName.match(/v2_(\w+)_(yearly|monthly)/);
    let interval: "month" | "year" = 'month';
    if (planMatch && planMatch.length > 2) {
        if (planMatch[2] === 'yearly') {
            interval = 'year';
        }
    }

    const [stripePromise, setStripePromise] = useState<Promise<Stripe | null>>(Promise.resolve(null));
    const [isReady, setIsReady] = useState(false);
    const { stripePubKey } = props;
    useEffect(() => {
        setStripePromise(loadStripe(stripePubKey));
        setIsReady(true);
    }, [stripePubKey]);

    return isReady ? (
        <Elements stripe={stripePromise} options={{
            mode: 'subscription',
            amount: props.productPriceInCents,
            currency: 'usd',
            paymentMethodTypes: ['card'],
            appearance: {
                theme: 'stripe',
                rules: {
                    '.Label': {
                        color: '#A3B3BF',
                        textTransform: 'uppercase',
                        fontSize: "12px",
                        fontWeight: '500',
                        paddingBottom: "3px",
                    }
                },
            }
        }}>
            <SignupForm user={props.user}
                statesAndCountries={props.statesAndCountries}
                isCaptchaEnabled={props.isCaptchaEnabled}
                isDevMode={props.isDevMode}
                interval={interval}
                product={props.product}
                productPriceInCents={props.productPriceInCents}
                planName={props.planName}
                showCouponByDefault={props.showCouponByDefault}
                promotionalMessage={props.promotionalMessage}
                defaultCouponCode={props.defaultCouponCode}
                discountedPriceCents={props.discountedPriceCents} />
        </Elements>
    ) : null;
}

export function SignupForm(props: {
    user: AuthUser,
    product: any,
    planName: PlanName,
    productPriceInCents: number,
    interval: "month" | "year",
    statesAndCountries: { [key: string]: { name: string, states: { [key: string]: string }, phonecode: string } },
    isCaptchaEnabled: boolean,
    isDevMode: boolean,
    showCouponByDefault?: boolean,
    promotionalMessage?: string,
    defaultCouponCode?: string,
    discountedPriceCents?: number,
}) {
    const formHelper = new FormHelper({});
    const { user, planName } = props;
    const businessTypes = BusinessType.getAll();

    const shouldDisableEmailField = shouldDisableSignupEmailField(user);
    const shouldDisablePasswordFields = shouldDisableSignupPasswordFields(user);
    const conditionalEmailDataToggle = shouldDisableEmailField ? "tooltip" : "";
    const conditionalEmailTooltip = shouldDisableEmailField ?
        `You&apos;re logged in as ${user.email}, this field can&apos;t be modified.` : "";
    const conditionalPasswordDataToggle = shouldDisablePasswordFields ? "tooltip" : "";
    const conditionalPasswordTooltip = shouldDisablePasswordFields ?
        `You&apos;re logged in as ${user.email}, this field can&apos;t be modified.` : "";

    const [path, setPath] = useState('');
    useEffect(() => {
        setPath(window.location.pathname);
    }, []);

    const elements = useElements();
    const stripe = useStripe();
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isValidating, setIsValidating] = useState<boolean>(false);
    const [errors, setErrors] = useState({});
    const [couponCode, setCouponCode] = useState<string>(props.defaultCouponCode || "");
    const [couponStatus, setCouponStatus] = useState<{
        validated: boolean | null;
        message: string;
        couponName: string;
    }>({ validated: null, message: "", couponName: "" });
    const [errorMessage, setErrorMessage] = useState<string>();
    const [turnstileToken, setTurnstileToken] = useState('');
    const turnstileRef = useRef<TurnstileWidgetHandle>(null);

    // Turnstile tokens are single-use. After any failed submit, reset the
    // widget so the user gets a fresh token before retrying.
    const resetTurnstile = () => {
        turnstileRef.current?.reset();
        setTurnstileToken('');
    };
    const [address, setAddress] = useState<{
        name: string;
        firstName?: string | undefined;
        lastName?: string | undefined;
        address: {
            line1: string;
            line2: string | null;
            city: string;
            state: string;
            postal_code: string;
            country: string;
        };
        phone?: string | undefined;
    }>();

    const handleError = (error: StripeError) => {
        setIsLoading(false);
        setIsValidating(false);
        setErrorMessage(error.message);
    };

    const handleCouponChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const code = event.target.value;
        setCouponCode(code);
        // Reset validation status when coupon code changes
        setCouponStatus({ validated: null, message: "", couponName: "" });
    }

    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) {
            setCouponStatus({
                validated: false,
                message: "Please enter a coupon code",
                couponName: ""
            });
            return;
        }

        setIsValidating(true);
        
        try {
            // Get email from form
            const emailInput = document.getElementById('email') as HTMLInputElement;
            const email = emailInput?.value || user.email || '';
            
            // Check if we're in dev mode
            const urlParams = new URLSearchParams(window.location.search);
            const dev = urlParams.get('dev') || '';
            
            const response = await fetch(
                `/api/coupon/validate?code=${encodeURIComponent(couponCode)}&email=${encodeURIComponent(email)}&dev=${dev}`
            );
            
            const data = await response.json();
            
            setCouponStatus({
                validated: data.status,
                message: data.desc,
                couponName: data.coupon
            });
        } catch {
            setCouponStatus({
                validated: false,
                message: "Error validating coupon",
                couponName: ""
            });
        } finally {
            setIsValidating(false);
        }
    }

    // Auto-validate coupon if it's pre-populated (e.g., from Welcome Back campaigns)
    useEffect(() => {
        if (props.defaultCouponCode && couponStatus.validated === null) {
            // Wait a bit for the form to be ready (email field needs to be populated)
            const timer = setTimeout(() => {
                handleApplyCoupon();
            }, 500);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    const handleEmailChange = debounce((event: React.ChangeEvent<HTMLInputElement>) => {
        const email = event.target.value;
        if (!email) {
            return;
        }

        const parsedEmail = z.string()
            .email({ message: "Please enter a valid email address" })
            .safeParse(email);

        if (!parsedEmail.success) {
            setErrors({ ...errors, email: parsedEmail.error.errors.map(e => e.message) })
            return;
        }


        setIsValidating(true);
        setErrors({ ...errors, email: [] });
        const testEmailText = <span style={{ color: 'red' }}>{' '}Testing email.</span>
        fetch(`/api/validate-email?email=${encodeURIComponent(parsedEmail.data)}&dev=${props.isDevMode}`).then(
            res => {
                res.json().then((response) => {
                    if (res.status === 200) {
                        const { isTestEmail, success, errors, canReactivate } = response;
                        if (!success) {
                            setErrors({
                                ...errors,
                                email: errors.map((e: string, idx: number) => {
                                    if (e === "Email already exists with active subscription.") {
                                        return <span key={idx}>
                                            This email is already associated with an active Adbeat account.
                                            To change plans, <a href="/login">click here to log in</a> and visit your profile page.
                                            {isTestEmail && testEmailText}
                                        </span>;
                                    }
                                    if (e === "Email already exists.") {
                                        // User exists but no active subscription - allow them to reactivate
                                        if (canReactivate) {
                                            return <span key={idx} style={{ color: '#2da1f8' }}>
                                                Welcome back! We found your account. Continue below to reactivate your subscription.
                                                {isTestEmail && testEmailText}
                                            </span>;
                                        }
                                        return <span key={idx}>
                                            This email is already associated with an Adbeat account.
                                            To change plans, <a href="/login">click here to log in.</a>
                                            {isTestEmail && testEmailText}
                                        </span>;
                                    }
                                    return e;
                                })
                            });
                        } else if (canReactivate) {
                            // Show positive message for returning customers
                            setErrors({
                                ...errors,
                                email: [
                                    <span key="reactivate" style={{ color: '#2da1f8' }}>
                                        Welcome back! Continue below to reactivate your subscription.
                                        {isTestEmail && testEmailText}
                                    </span>
                                ]
                            });
                        }
                        setIsValidating(false);
                    }
                });
            }
        );
    }, 500);

    const handlePasswordChange = () => {
        const passwordSchema = FormHelper.passwordSchema();
        const parsedPassword = passwordSchema.safeParse({
            password,
            confirmpassword: confirmPassword,
        });

        if (!parsedPassword.success) {
            setErrors({ ...errors, password: parsedPassword.error.errors.map(e => e.message) });
            return;
        }

        setErrors({ ...errors, password: [] });
    }

    const handleSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();

        if (!elements || !stripe) {
            return;
        }

        if (props.isCaptchaEnabled && !turnstileToken) {
            setIsLoading(false);
            setErrorMessage("Please complete the verification challenge before submitting.");
            return;
        }

        // Validate coupon if entered but not validated
        if (couponCode && couponStatus.validated === null) {
            await handleApplyCoupon();
            setIsLoading(false);
            setErrorMessage("Please validate your coupon code before proceeding.");
            return;
        }

        // Prevent submission if coupon validation failed
        if (couponCode && couponStatus.validated === false) {
            setIsLoading(false);
            setErrorMessage("Please enter a valid coupon code or remove it to continue.");
            return;
        }
        // Clear previous errors when starting new submission
        setErrorMessage(undefined);
        setErrors({});
        setIsLoading(true);
        setErrorMessage(undefined);
        setErrors({});
        const { error: submitError } = await elements.submit();

        if (submitError) {
            handleError(submitError);
            return;
        }

        const formData = new FormData(event.target as HTMLFormElement);
        const urlParams = new URLSearchParams(window.location.search);
        const smokeCaptchaBypassToken = urlParams.get('smokeCaptchaBypassToken') || '';

        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };

        if (smokeCaptchaBypassToken) {
            headers['x-smoke-captcha-bypass'] = smokeCaptchaBypassToken;
        }

        const res = await fetch('/api/create-subscription', {
            method: "POST",
            body: JSON.stringify({
                ...Object.fromEntries(formData.entries()),
                coupon_code: couponStatus.validated ? couponStatus.couponName : '',
                'cf-turnstile-response': turnstileToken,
                address,
                isDevMode: props.isDevMode
            }),
            headers,
        });

        const response = await res.json();

        if (res.status !== 200) {
            setIsLoading(false);
            setErrors(response.errors);
            resetTurnstile();
            return;
        }

        const { type, guid, clientSecret } = response;
        
        logDebug('Received from backend', { type, guid, hasClientSecret: !!clientSecret });
        
        if (type === "setup") {
            const { error } = await stripe.confirmSetup({
                elements,
                clientSecret,
                confirmParams: {
                    return_url: `${window.location.origin}/receipt/${guid}`,
                },
            });

            if (error) {
                logError(error, { context: 'Stripe setup confirmation error' });
                handleError(error);
                return;
            }
        } else {
            const { error } = await stripe.confirmPayment({
                elements,
                clientSecret,
                confirmParams: {
                    return_url: `${window.location.origin}/receipt/${guid}`,
                    receipt_email: formData.get('email') as string,
                },
            });

            if (error) {
                logError(error, { context: 'Stripe payment confirmation error' });
                handleError(error);
                return;
            }
        }
        logDebug('Stripe confirmation succeeded, should redirect now');
    };


    return (
        <form id="adbeat-signup-form" noValidate={true} onSubmit={handleSubmit}
            className="max-w-[1300px] mx-auto">
            <input type="hidden" name="affId" id="affId" value="" />
            <input type="hidden" name="affCookie" id="affCookie" />
            <input type="hidden" name="stripeToken" id="stripeToken" />
            <input type="hidden" name="productHandle" defaultValue={props.product.handle} />
            <input type="hidden" name="productPrice" defaultValue={props.product.price_in_cents} />
            {planName.startsWith('v2_advanced') &&
                <div className={clsx(
                    "bg-[#f8e031]",
                    "rounded",
                    "text-center",
                    "text-[#211e05]",
                    "uppercase",
                    avenirNextMedium.className,
                    "font-[700]",
                    "py-[3px]",
                    "mt-[30px]",
                    "mb-[10px]",
                    "lg:hidden",
                )}>Most Popular</div>
            }
            <div className={clsx(
                "grid",
                "grid-cols-1",
                "lg:grid-cols-3",
            )}>
                <div className={clsx(
                    "col-span-1",
                    "md:col-span-2",
                    planName.startsWith('v2_advanced') ? "lg:mt-[60px]" : "mt-[30px]",
                )}>
                    <div className={clsx(
                        "bg-white",
                        "shadow",
                        "overflow-hidden",
                        "rounded-[10px]",
                    )}>
                        <h3 className={clsx(
                            "text-[#44494e]",
                            "text-center",
                            "text-[13px]",
                            avenirNextBold.className,
                            "uppercase",
                            "py-[18px]",
                            "md:py-[26px]",
                            "px-[15px]",
                            "rounded-tl",
                            "bg-[#fafbfc]",
                            "border-b",
                            "border-[#e9eff4]",
                        )}>
                            Create your Adbeat account
                        </h3>
                        <div className={clsx(
                            "rounded-bl",
                        )}>
                            <div className={clsx(
                                "p-[15px]",
                                "sm:px-[70px]",
                                "pt-[40px] pb-[30px]",
                            )}>
                                <div className="form text-left">
                                    {(user.is_free && user.is_logged_in) &&
                                        <>
                                            <p className="text-[#354052] text-[14px] mb-[5px]">You&apos;re logged in as {user.email}</p>
                                            <p className="text-[#354052] text-[14px] mb-[20px]">Continue below to upgrade this account. Want to sign up with a different email?
                                                {' '}
                                                <a href={`/logout?next=${path}`} className="text-blue-500 hover:underline">
                                                    Click here to log out
                                                </a>
                                            </p>
                                        </>
                                    }
                                    {(!user.is_free && user.is_logged_in && user.stripe_status === 'active') &&
                                        <>
                                            <p className="text-[#354052] text-[14px] mb-[5px]">You&apos;re logged in as {user.email}</p>
                                            <p className="text-[#354052] text-[14px] mb-[20px]">
                                                Want to change plans?
                                                {' '}
                                                <Link href="/user-profile/upgrade" className="text-blue-500 hover:underline">Click here to visit your profile page</Link>
                                            </p>
                                        </>
                                    }

                                    <div className={clsx(
                                        "grid",
                                        "grid-cols-1",
                                        "md:grid-cols-2",
                                        "gap-[30px]",
                                        "mb-[30px]",
                                    )}>
                                        <div className={clsx(
                                            formGroupClassName,
                                            "has-feedback",
                                            "relative",
                                        )}
                                            data-toggle={conditionalEmailDataToggle}
                                            title={conditionalEmailTooltip}>
                                            <label htmlFor="email" className={labelClassName}>Email address</label>
                                            {shouldDisableEmailField && (
                                                <input type="hidden" name="email" value={user.email} />
                                            )}
                                            <input type="text"
                                                name={shouldDisableEmailField ? undefined : "email"}
                                                placeholder=""
                                                id="email"
                                                className={clsx(
                                                    inputClassName,
                                                    formHelper.hasErrorFeedback('email', 'has-error', { errors })
                                                )}
                                                suppressHydrationWarning={true}
                                                autoComplete="off"
                                                data-lpignore="true"
                                                data-1p-ignore="true"
                                                data-bwignore="true"
                                                readOnly={shouldDisableEmailField}
                                                value={shouldDisableEmailField ? user.email : undefined}
                                                onChange={handleEmailChange}
                                                disabled={shouldDisableEmailField} />
                                            {formHelper.hasErrorDetails('email', { errors })}
                                            {isValidating && <span className={clsx(
                                                glyphiconFont.className,
                                                "absolute",
                                                "right-[15px]",
                                                "top-[30px]",
                                            )}>;&#xe031;</span>}
                                        </div>
                                        <div className="hidden md:block"></div>
                                        <div className={clsx(
                                            formGroupClassName,
                                            "has-feedback",
                                            "relative",
                                        )}
                                            data-toggle={conditionalPasswordDataToggle}
                                            title={conditionalPasswordTooltip}>
                                            <label htmlFor="password" className={labelClassName}>Password</label>
                                            <input type="password" placeholder="" name="password" id="password"
                                                onBlur={handlePasswordChange}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className={clsx(
                                                    inputClassName,
                                                    formHelper.hasErrorFeedback('password', 'has-error', { errors })
                                                )}
                                                disabled={shouldDisablePasswordFields} />
                                            {formHelper.hasErrorDetails('password', { errors })}
                                        </div>
                                        <div className={clsx(
                                            formGroupClassName,
                                            "has-feedback",
                                        )}
                                            data-toggle={conditionalPasswordDataToggle}
                                            title={conditionalPasswordTooltip}>
                                            <label htmlFor="confirmpassword" className={labelClassName}>Confirm Password</label>
                                            <input type="password" placeholder="" name="confirmpassword" id="confirmpassword"
                                                className={clsx(
                                                    inputClassName,
                                                    formHelper.hasErrorFeedback('confirmpassword', 'has-error', { errors })
                                                )}
                                                onBlur={handlePasswordChange}
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                disabled={shouldDisablePasswordFields} />
                                            {formHelper.hasErrorDetails('confirmpassword', { errors })}
                                        </div>

                                        <div className={clsx(
                                            formGroupClassName,
                                            "has-feedback",
                                        )}>

                                            <select id="business_type" name="business_type"
                                                className={clsx(
                                                    inputClassName,
                                                    formHelper.hasErrorFeedback('business_type', 'has-error', { errors })
                                                )} >
                                                {Object.keys(businessTypes).map(key => (
                                                    <option key={key || "default"} value={key}>{businessTypes[key]}</option>
                                                ))}
                                            </select>
                                            {formHelper.hasErrorDetails('business_type', { errors })}
                                        </div>
                                        {(process.env.NEXT_PUBLIC_SHOW_SIGNUP_COUPON || props.showCouponByDefault) && (
                                            <div className={clsx(
                                                formGroupClassName,
                                                "md:col-span-2",
                                            )}>
                                                {props.promotionalMessage && (
                                                    <div className={clsx(
                                                        "mb-[10px]",
                                                        "text-[14px]",
                                                        "text-[#4a9eff]",
                                                        avenirNextMedium.className,
                                                    )}>
                                                        {props.promotionalMessage}
                                                    </div>
                                                )}
                                                <label htmlFor="coupon_code" className={labelClassName}>Coupon Code {props.showCouponByDefault ? '' : '(Optional)'}</label>
                                                <div className="flex gap-[10px]">
                                                    <input
                                                        type="text"
                                                        name="coupon_code"
                                                        id="coupon_code"
                                                        placeholder="Enter coupon code"
                                                        value={couponCode}
                                                        onChange={handleCouponChange}
                                                        className={clsx(inputClassName, "flex-1")}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={handleApplyCoupon}
                                                        disabled={isValidating || !couponCode.trim()}
                                                        className={clsx(
                                                            "px-[20px]",
                                                            "py-[10px]",
                                                            "rounded",
                                                            "text-white",
                                                            "min-w-[100px]",
                                                            isValidating || !couponCode.trim()
                                                                ? "bg-gray-400 cursor-not-allowed"
                                                                : "bg-[#2da1f8] hover:bg-[#1991ed]"
                                                        )}
                                                    >
                                                        {isValidating ? "Validating..." : "Apply"}
                                                    </button>
                                                </div>
                                                {couponStatus.message && (
                                                    <div
                                                        className={clsx(
                                                            "mt-[5px]",
                                                            "text-[14px]",
                                                            couponStatus.validated ? "text-green-600" : "text-red-600"
                                                        )}
                                                    >
                                                        {couponStatus.message}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div className={clsx(
                                            "text-[#86939e]",
                                            "md:col-span-2",
                                        )}>
                                            <small>
                                                This Adbeat plan is a recurring subscription and you will be billed
                                                {' '}{formatMoney(props.productPriceInCents)} every {props.interval}
                                                {' '}until you cancel.
                                            </small>
                                        </div>
                                    </div>

                                    <h3 className={headingClassName}>What&apos;s your billing info?</h3>

                                    <div className={clsx(
                                        "py-[20px]",
                                    )}>
                                        <AddressElement options={{
                                            mode: 'billing',
                                            fields: {
                                                phone: 'always',
                                            },
                                            validation: {
                                                phone: {
                                                    required: 'always',
                                                },
                                            },
                                            display: {
                                                name: "split",
                                            }
                                        }} onChange={(event) => {
                                            if (event.complete) {
                                                setAddress(event.value);
                                            }
                                        }} />
                                        <PaymentElement />
                                    </div>
                                    <div className={clsx(
                                        "form-group",
                                        "mb-[20px]",
                                    )}>
                                        <input type="checkbox" name="terms" id="terms" value="1"
                                            className={clsx(
                                                "relative",
                                                "top-[3px]",
                                                formHelper.hasErrorFeedback('terms', 'has-error', { errors }),
                                            )} />
                                        {' '}
                                        <label htmlFor="terms" className={clsx(
                                            labelClassName,
                                            "w-[95%]",
                                        )}>
                                            I agree to the Adbeat
                                            {' '}
                                            <Link href="/terms" target="_blank" className="text-blue-500 hover:underline">Terms of Use</Link>
                                            {' '}
                                            and subscription conditions detailed above.
                                        </label>
                                        <br />
                                        {formHelper.hasErrorDetails('terms', { errors })}
                                    </div>

                                    {props.isCaptchaEnabled &&
                                        <TurnstileWidget
                                            ref={turnstileRef}
                                            action="signup"
                                            onTokenChange={setTurnstileToken}
                                        />
                                    }

                                    {!isLoading &&
                                        <AdbeatButton
                                            type="submit"
                                            name="order"
                                            id="order"
                                            text="Get Adbeat"
                                            className={clsx(
                                                'bg-gradient-to-b',
                                                'border-[#2da1f8]',
                                                'from-[#2da1f8]',
                                                'to-[#1991ed]',
                                                'text-[16px]',
                                                'min-w-[192px]',
                                            )}
                                        />
                                    }
                                    {formHelper.hasErrors('input') || errorMessage &&
                                        <span id="orderValidation" className={clsx(
                                            "text-red-500",
                                            "block",
                                            "mt-[10px]",
                                        )}>
                                            There&apos;s a problem with some of the information you&apos;ve entered.
                                        </span>
                                    }
                                    {isLoading &&
                                        <Image
                                            width={220}
                                            height={19}
                                            unoptimized={true}
                                            id="orderAnim" 
                                            alt="Loading animation" 
                                            src={ajaxAnimatedBar} />
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={clsx(
                    "col-span-1",
                    "max-w-[400px]",
                    "mx-auto",
                    "w-full",
                    "md:max-w-none",
                    "md:mx-0",
                    "md:relative",
                    "md:right-[10px]",
                    "xl:right-[25px]",
                )}>
                    {planName.startsWith('v2_advanced') &&
                        <div className={clsx(
                            "bg-[#f8e031]",
                            "rounded",
                            "text-center",
                            "text-[#211e05]",
                            "uppercase",
                            avenirNextMedium.className,
                            "font-[700]",
                            "py-[3px]",
                            "max-w-[400px]",
                            "mx-[10px]",
                            "sm:mx-auto",
                            "hidden",
                            "lg:block",
                        )}>Most Popular</div>
                    }

                    <PlanDescription product={props.product} discountedPriceCents={props.discountedPriceCents} />
                </div>
            </div>
        </form>
    );
};
